import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { InvoiceStatusEnum } from "@/lib/enum/invoice-status.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICustomViewableEntity } from "@/lib/interfaces/custom-viewable-entity.interface";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { UpdateDto } from "@/lib/utility/data/update-dto.interface";
import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import { GroupGoToHelper } from "@/lib/utility/group.go-to-helper";
import { InvoiceGoToHelper } from "@/lib/utility/invoice.go-to-helper";
import { ISignDocument, SignDocument } from "@/models/sign-document.entity";
import invoiceService from "@/services/mrfiktiv/services/invoice-service";
import {
  MrfiktivAccountingRecordItemViewmodelGen,
  MrfiktivCreateInboundInvoiceDtoGen,
  MrfiktivCreateOutboundInvoiceDocumentDtoGen,
  MrfiktivCreateOutboundInvoiceDtoGen,
  MrfiktivInvoiceControllerCreatePayloadGen,
  MrfiktivInvoiceControllerUpdatePayloadGen,
  MrfiktivInvoicePeriodViewModelGen,
  MrfiktivInvoiceViewModelGen,
  MrfiktivUpdateInboundInvoiceDtoGen,
  MrfiktivUpdateOutboundInvoiceDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { CompanyDataAccessLayer } from "@/store/modules/access-layers/company.access-layer";
import { FleetDataAccessLayer } from "@/store/modules/access-layers/fleet.access-layer";
import { InvoiceDataAccessLayer } from "@/store/modules/access-layers/invoice.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { VehicleAccessLayer } from "@/store/modules/access-layers/vehicle.access-layer";
import VueRouter from "vue-router";
import { Address } from "./address.entity";
import { Company, ICompany } from "./company.entity";
import { CustomFieldValue, ICustomFieldValue } from "./custom-field-value.entity";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IReport, Report } from "./report.entity";
import { handleError } from "@/lib/utility/handleError";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";

/**
 * The "direction" of document
 */
export enum DocumentDirectionEnum {
  /**
   * Inbound documents. Usually the partner is the `buyer` or "PURCHASE" on inbound documents.
   * "Eingangsrechnung" is typically translated as a "purchase invoice" or "supplier invoice".
   * This document is received from a vendor or supplier for goods or services purchased.
   */
  INBOUND = "inbound",

  /**
   * Outbound documents. Usually the partner is the `seller` or "SALE" on outbound documents.
   * "Ausgangsrechnung" is generally referred to as a "sales invoice" or "customer invoice".
   * This is the document that the partner issues to the customer to bill them for delivered goods or rendered services.
   */
  OUTBOUND = "outbound"
}

/**
 * The type of the invoice.
 * An invoice must have a Invoice type code (BT-3) based on XRechnung standard.
 * Based on DE-R-017. The element "Invoice type code" (BT-3) should only contain the following values from code list UNTDID 1001:
 * - 326 (Partial invoice),
 * - 380 (Commercial invoice),
 * - 384 (Corrected invoice),
 * - 389 (Self-billed invoice),
 * - 381 (Credit note),
 * - 875 (Partial construction invoice),
 * - 876 (Partial final construction invoice),
 * - 877 (Final construction invoice)
 * @see https://docs.peppol.eu/poacc/billing/3.0/codelist/UNCL1001-inv/
 * @see https://xeinkauf.de/xrechnung/ urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0
 */
export enum InvoiceTypeEnum {
  /**
   * Partial invoice. Code 326
   */
  PARTIAL = "partial",

  /**
   * Commercial Invoice. Code 380.
   * Document/message claiming payment for goods or services supplied under conditions agreed between seller and buyer.
   */
  COMMERCIAL = "commercial",

  /**
   * Corrected Invoice. Code 384.
   */
  CORRECTED = "corrected",

  /**
   * Self billed invoice. Code 389.
   */
  SELF_BILLED = "self_billed",

  /**
   * Credit note. Code 381.
   */
  CREDIT_NOTE = "credit_note"
}

@IsFilterable
export class InvoiceBase
  implements
    IEntity<IInvoice, MrfiktivInvoiceControllerUpdatePayloadGen>,
    ICustomViewableEntity<IInvoice, MrfiktivInvoiceControllerUpdatePayloadGen>,
    Omit<MrfiktivInvoiceViewModelGen, "values" | "buyer" | "seller">,
    Partial<Pick<MrfiktivInvoiceViewModelGen, "buyer" | "seller">>,
    UpdateDto<IInvoice>
{
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoice.direction",
    config: {
      items: Object.values(DocumentDirectionEnum).map((v) => {
        return {
          text: `enums.DocumentDirectionEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  direction: DocumentDirectionEnum;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoice.type",
    config: {
      items: Object.values(InvoiceTypeEnum).map((v) => {
        return {
          text: `enums.InvoiceTypeEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  type: InvoiceTypeEnum;

  /** Id of the invoice object */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.id"
  })
  id: string;

  /** Invoice number */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.number"
  })
  number: string;

  /** Invoice date */
  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.invoice.date"
  })
  get dateFormable(): string {
    if (!this.date) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.date));
  }
  set dateFormable(date: string) {
    if (date) {
      this.date = new Date(date).toISOString();
    }
  }

  date: string;

  /** Group ID */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.groupId"
  })
  groupId?: string;

  /** Currency */
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoice.currency"
  })
  currency: CurrencyEnum;

  /** Buyer */
  @FilterConfig({
    type: InvoiceParty
  })
  buyer?: IInvoiceParty;

  buyerCompany?: ICompany;

  /** Supplier */
  @FilterConfig({
    type: InvoiceParty
  })
  seller?: IInvoiceParty;

  sellerCompany?: ICompany;

  /** Due Date */
  get dueFormable(): string {
    if (!this.due) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.due));
  }
  set dueFormable(date: string) {
    if (date) {
      this.due = new Date(date).toISOString();
    }
  }

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.invoice.due"
  })
  due?: string;

  servicePeriod?: MrfiktivInvoicePeriodViewModelGen | undefined;

  /** Service period start date */
  get servicePeriodStart(): string {
    if (!this.servicePeriod?.start) {
      return "";
    }

    return formatYearsMonthDay(new Date(this.servicePeriod.start));
  }
  set servicePeriodStart(date: string) {
    if (date) {
      if (this.servicePeriod) {
        this.servicePeriod.start = new Date(date).toISOString();
      } else {
        this.servicePeriod = {
          start: new Date(date).toISOString()
        };
      }
    }
  }

  /** Total Amount */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.total"
  })
  total?: number;

  /** Amount Due */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.amountDue"
  })
  amountDue?: number;

  /** External ID */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.externalId"
  })
  externalId?: string;

  /** Header */
  header?: string;

  /** Footer */
  footer?: string;

  /** Subtotal */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.subTotal"
  })
  subTotal?: number;

  /** Tax Total */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.taxTotal"
  })
  taxTotal?: number;

  /** Amount Paid */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.amountPaid"
  })
  amountPaid?: number;

  /** Amount Remaining */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.amountRemaining"
  })
  amountRemaining?: number;

  /** Notes */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.notes"
  })
  notes?: string[];

  /** Notes */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.notes"
  })
  lineItems: IInvoiceLine[];

  /** The debtor number */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.debtorAccountNumber"
  })
  debtorAccountNumber?: number;

  /** The creditor number */

  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.creditorAccountNumber"
  })
  creditorAccountNumber?: number;

  /** Document */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.documentIds",
    config: {
      itemCallback: () => SignDocumentDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-document",
      refType: BackendResourceEnum.DOCUMENT
    }
  })
  documentId?: string;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.invoice.isBilled"
  })
  isBilled: boolean;

  document?: ISignDocument;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.reportId",
    config: {
      itemCallback: () => ReportDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-report",
      refType: BackendResourceEnum.REPORT
    }
  })
  reportId?: string;

  report?: IReport;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.fleetId",
    config: {
      itemCallback: () => FleetDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      itemText: "title",
      component: "refs-fleet",
      refType: BackendResourceEnum.FLEET
    }
  })
  fleetId?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.vehicleId",
    config: {
      itemCallback: () => VehicleAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-vehicle",
      refType: BackendResourceEnum.VEHICLE
    }
  })
  vehicleId?: string;

  partnerId: string;

  userId: string;

  @FilterConfig({
    displayName: "objects.ticket.state",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(InvoiceStatusEnum).map((e) => {
        return {
          text: `enums.InvoiceStatusEnum.${e}`,
          value: e
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    width: "85"
  })
  status: InvoiceStatusEnum;

  timestamp: ITimestamp;

  accountRecord?: MrfiktivAccountingRecordItemViewmodelGen[];

  get isUpdateable() {
    if (this.document?.isInternal) {
      return true;
    }

    return (
      !this.isBilled &&
      (this.direction === DocumentDirectionEnum.INBOUND ||
        (this.direction === DocumentDirectionEnum.OUTBOUND && this.status === InvoiceStatusEnum.DRAFT))
    );
  }

  loading = false;

  get projectId() {
    return this.groupId;
  }

  get titleReadable() {
    return `${this.number}`;
  }

  // A map of states and colors
  INVOICE_COLOR_BY_STATUS = new Map([
    [InvoiceStatusEnum.ERROR, "error"],
    [InvoiceStatusEnum.DRAFT, "grey"],
    [InvoiceStatusEnum.OPEN, "primary"],
    [InvoiceStatusEnum.PAID, "green"],
    [InvoiceStatusEnum.VOIDED, "grey darken-3"]
  ]);

  get statusColor() {
    return this.INVOICE_COLOR_BY_STATUS.get(this.status);
  }

  get totalGross() {
    return this.lineItems.reduce((acc, item) => acc + (1 + item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get totalNet() {
    return this.lineItems.reduce((acc, item) => acc + item.amount * item.quantity, 0);
  }

  get totalTax() {
    return this.lineItems.reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get taxRates() {
    return Array.from(new Set(this.lineItems.map((line) => line.taxRate))).filter((rate) => rate !== 0);
  }

  get isInbound() {
    return this.direction === DocumentDirectionEnum.INBOUND;
  }

  get isOutbound() {
    return this.direction === DocumentDirectionEnum.OUTBOUND;
  }

  get isCreditNote() {
    return this.type === InvoiceTypeEnum.CREDIT_NOTE;
  }

  values?: ICustomFieldValue[];

  refs?: IReference[];

  constructor(data?: Partial<MrfiktivInvoiceViewModelGen> | IInvoice) {
    this.id = data?.id ?? "";
    this.direction = (data?.direction as DocumentDirectionEnum) ?? DocumentDirectionEnum.INBOUND;
    this.type = (data?.type as InvoiceTypeEnum) ?? InvoiceTypeEnum.COMMERCIAL;

    if (data?.buyer) {
      this.buyer = new InvoiceParty(data?.buyer);
    }

    if (data?.seller) {
      this.seller = new InvoiceParty(data?.seller);
    }

    this.externalId = data?.externalId;
    this.groupId = data?.groupId;
    this.userId = data?.userId ?? "";
    this.values = CustomFieldValue.buildCustomFieldValues(data?.values || []);
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map((ref) => new Reference(ref)));
    this.status = (data?.status as InvoiceStatusEnum) ?? InvoiceStatusEnum.DRAFT;
    this.number = data?.number ?? "";
    this.date = data?.date ?? new Date().toISOString();
    this.servicePeriod = data?.servicePeriod ?? {
      start: this.date
    };
    this.currency = (data?.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.header = data?.header;
    this.lineItems = ((data?.lineItems as IInvoiceLine[]) ?? []).map((line) => new InvoiceLine(line));
    this.footer = data?.footer;
    this.subTotal = data?.subTotal;
    this.taxTotal = data?.taxTotal;
    this.total = data?.total;
    this.amountDue = data?.total;
    this.amountPaid = data?.amountPaid;
    this.amountRemaining = data?.amountRemaining;
    this.isBilled = data?.isBilled || false;
    this.due = data?.due ?? new Date().toISOString();
    this.notes = data?.notes;
    this.accountRecord = data?.accountRecord;
    this.creditorAccountNumber = data?.creditorAccountNumber;
    this.debtorAccountNumber = data?.debtorAccountNumber;
    this.documentId = data?.documentId;
    this.reportId = data?.reportId;
    this.report = data?.report ? new Report(data?.report) : undefined;
    this.fleetId = data?.fleetId;
    this.vehicleId = data?.vehicleId;
    this.partnerId = data?.partnerId ?? "";
    this.timestamp = new Timestamp(data?.timestamp);
  }

  map(data: Partial<MrfiktivInvoiceViewModelGen>): void {
    this.id = data.id ?? "";
    this.direction = (data?.direction as DocumentDirectionEnum) ?? DocumentDirectionEnum.INBOUND;
    this.type = (data?.type as InvoiceTypeEnum) ?? InvoiceTypeEnum.COMMERCIAL;
    this.externalId = data.externalId;
    this.groupId = data.groupId;
    this.userId = data.userId ?? "";
    this.values = CustomFieldValue.buildCustomFieldValues(data.values || []);
    this.refs = Reference.filterDuplicates((data.refs ?? []).map((ref) => new Reference(ref)));
    this.status = (data.status as InvoiceStatusEnum) ?? InvoiceStatusEnum.DRAFT;
    this.number = data.number ?? "";
    this.date = data?.date ?? new Date().toISOString();
    this.servicePeriod = data?.servicePeriod ?? {
      start: this.date
    };
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.buyer = new InvoiceParty(data.buyer);
    this.seller = new InvoiceParty(data.seller);
    this.header = data.header;
    this.lineItems = (data.lineItems ?? []).map((line) => new InvoiceLine(line));
    this.footer = data.footer;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.total = data.total;
    this.amountDue = data.amountDue;
    this.amountPaid = data.amountPaid;
    this.amountRemaining = data.amountRemaining;
    this.isBilled = data?.isBilled || false;
    this.due = data.due ?? new Date().toISOString();
    this.notes = data.notes;
    this.accountRecord = data.accountRecord;
    this.creditorAccountNumber = data?.creditorAccountNumber;
    this.debtorAccountNumber = data?.debtorAccountNumber;
    this.documentId = data?.documentId;
    this.reportId = data?.reportId;
    this.report = data.report ? new Report(data?.report) : undefined;
    this.fleetId = data?.fleetId;
    this.vehicleId = data.vehicleId;
    this.partnerId = data.partnerId ?? "";
    this.timestamp = new Timestamp(data.timestamp);
  }

  async create(): Promise<this> {
    let createInvoiceDto: MrfiktivInvoiceControllerCreatePayloadGen;

    if (!this.buyer) {
      throw Error("Buyer missing");
    }
    if (!this.seller) {
      throw Error("Seller missing");
    }

    if (this.isInbound) {
      const dto: MrfiktivCreateInboundInvoiceDtoGen = {
        number: this.number,

        direction: this.direction,
        type: this.type,
        currency: this.currency,

        buyer: this.buyer.dto,
        seller: this.seller.dto,

        lineItems: this.lineItems.map((line) => line.dto),

        due: this.due,
        servicePeriod: this.servicePeriod,

        footer: this.footer,
        header: this.header,
        notes: this.notes?.filter((n) => n && n.trim() !== ""),
        date: this.date,

        accountRecord: this.accountRecord,

        externalId: this.externalId,
        documentId: this.documentId,

        vehicleId: this.vehicleId,
        reportId: this.reportId,
        fleetId: this.fleetId,
        groupId: this.groupId,
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
        refs: Reference.filterDuplicates(this.refs as IReference[])
      };
      createInvoiceDto = dto;
    } else {
      const dto: MrfiktivCreateOutboundInvoiceDtoGen = {
        direction: this.direction,
        type: this.type,
        currency: this.currency,

        buyer: this.buyer.dto,
        seller: this.seller.dto,

        lineItems: this.lineItems.map((line) => line.dto),

        due: this.due,
        servicePeriod: this.servicePeriod ?? {
          start: this.date
        },

        footer: this.footer,
        header: this.header,
        notes: this.notes?.filter((n) => n && n.trim() !== ""),
        date: this.date,

        accountRecord: this.accountRecord,

        externalId: this.externalId,

        vehicleId: this.vehicleId,
        reportId: this.reportId,
        fleetId: this.fleetId,
        groupId: this.groupId,
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
        refs: Reference.filterDuplicates(this.refs as IReference[])
      };

      createInvoiceDto = dto;
    }

    const res = await invoiceService.create(this.partnerId, createInvoiceDto);
    this.map(res);
    InvoiceDataAccessLayer.set(this);

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await invoiceService.getOne(this.partnerId, this.id);
      this.map(res);

      if (this.documentId) {
        this.document = SignDocumentDataAccessLayer.maps.id.get(this.documentId)[0];
        if (!this.document) {
          this.document = new SignDocument({ id: this.documentId, partnerId: this.partnerId });
        }

        try {
          await this.document.fetch();
        } catch (error) {
          handleError(error);
          this.document = undefined;
        }
      }

      if (this.isInbound && this.seller?.ref?.refId && this.seller.ref?.refType === ResourceEnum.COMPANY) {
        this.sellerCompany = CompanyDataAccessLayer.maps.id.get(this.seller.ref.refId)[0];
        if (!this.sellerCompany) {
          this.sellerCompany = new Company({ id: this.seller.ref.refId, partnerId: this.partnerId });
        }

        await this.sellerCompany?.fetch();
      }

      if (this.isOutbound && this.buyer?.ref?.refId && this.buyer.ref?.refType === ResourceEnum.COMPANY) {
        this.buyerCompany = CompanyDataAccessLayer.maps.id.get(this.buyer.ref.refId)[0];
        if (!this.buyerCompany) {
          this.buyerCompany = new Company({ id: this.buyer.ref.refId, partnerId: this.partnerId });
        }

        await this.buyerCompany?.fetch();

        console.log("fetched buyerCompany", this.buyerCompany);
      }

      InvoiceDataAccessLayer.set(this);
    } catch (e) {
      InvoiceDataAccessLayer.delete(this);
      throw e;
    } finally {
      this.loading = false;
    }
    return this;
  }

  async updatePartial(dto: Partial<MrfiktivInvoiceControllerUpdatePayloadGen>): Promise<this> {
    if (dto.values) {
      dto.values = CustomFieldValue.buildCustomFieldValuesDto((dto.values as ICustomFieldValue[]) || []);
    }
    const res = await invoiceService.update(this.partnerId, this.id, dto);
    this.map(res);
    InvoiceDataAccessLayer.set(this);
    return this;
  }

  async update(): Promise<this> {
    if (!this.buyer) {
      throw Error("Buyer missing");
    }
    if (!this.seller) {
      throw Error("Seller missing");
    }

    this.loading = true;

    try {
      let update: MrfiktivInvoiceControllerUpdatePayloadGen;

      if (this.isInbound) {
        if (!this.documentId) {
          throw Error("Document missing");
        }

        const dto: MrfiktivUpdateInboundInvoiceDtoGen = {
          number: this.number,

          direction: this.direction,
          type: this.type,
          currency: this.currency,

          buyer: this.buyer.dto,
          seller: this.seller.dto,

          lineItems: this.lineItems.map((line) => line.dto),

          due: this.due,
          footer: this.footer,
          header: this.header,
          notes: this.notes?.filter((n) => n && n.trim() !== ""),
          date: this.date,
          servicePeriod: this.servicePeriod,

          accountRecord: this.accountRecord,

          externalId: this.externalId,
          documentId: this.documentId,

          vehicleId: this.vehicleId,
          reportId: this.reportId,
          fleetId: this.fleetId,
          groupId: this.groupId,
          values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
          refs: Reference.filterDuplicates(this.refs as IReference[])
        };
        update = dto;
      } else {
        const dto: MrfiktivUpdateOutboundInvoiceDtoGen = {
          direction: this.direction,
          type: this.type,
          currency: this.currency,

          buyer: this.buyer.dto,
          seller: this.seller.dto,

          lineItems: this.lineItems.map((line) => line.dto),

          due: this.due,
          footer: this.footer,
          header: this.header,
          notes: this.notes?.filter((n) => n && n.trim() !== ""),
          date: this.date,
          servicePeriod: this.servicePeriod,

          accountRecord: this.accountRecord,

          externalId: this.externalId,

          vehicleId: this.vehicleId,
          reportId: this.reportId,
          fleetId: this.fleetId,
          groupId: this.groupId,
          values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
          refs: Reference.filterDuplicates(this.refs as IReference[])
        };

        update = dto;
      }

      await this.updatePartial(update);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
    return this;
  }

  async delete(): Promise<void> {
    const res = await invoiceService.delete(this.partnerId, this.id);
    this.map(res);
    InvoiceDataAccessLayer.delete(this);
  }

  async print(): Promise<this> {
    const res = await invoiceService.print(this.partnerId, this.id);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.fetch(); // loads the document for documentId

    return this;
  }

  async addDocument(data: MrfiktivCreateOutboundInvoiceDocumentDtoGen): Promise<this> {
    const res = await invoiceService.addDocument(this.partnerId, this.id, data);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.fetch(); // loads the document for documentId

    return this;
  }

  async loadPreview(): Promise<Uint8Array> {
    return await invoiceService.generatePdfPreview(this.partnerId, this.id);
  }

  /**
   * Calculates all the line items for a given taxRate
   * @param taxRate the taxRate to calculate tax for
   * @returns
   */
  calcItemsPerTaxRate(taxRate: number): number {
    return this.lineItems
      .filter((l) => l.taxRate === taxRate)
      .reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  goTo(router: VueRouter) {
    return {
      customView: (newTab = false) => {
        if (this.groupId)
          new GroupGoToHelper(router).goToGroupTypeCustomView({
            partnerId: this.partnerId,
            type: BackendResourceEnum.INVOICE,
            groupId: this.groupId,
            viewId: "0",
            newTab
          });
      },
      table: (newTab = false) =>
        new InvoiceGoToHelper(router).goToInvoiceInboundTable({
          partnerId: this.partnerId,
          newTab
        }),
      detail: (newTab = false) =>
        new InvoiceGoToHelper(router).goToInvoiceDetail({
          partnerId: this.partnerId,
          invoiceId: this.id,
          newTab
        })
    };
  }

  /**
   *
   * @param company
   */
  set sellerFromCompany(company: ICompany) {
    const invoiceParty = new InvoiceParty();

    if (!company) {
      return;
    }

    invoiceParty.name = "";
    if (company.firstName) {
      invoiceParty.name = company.firstName;
    }
    if (company.lastName) {
      if (invoiceParty.name) {
        invoiceParty.name += " ";
      }
      invoiceParty.name += company.lastName;
    }
    if (company.company) {
      if (invoiceParty.name) {
        invoiceParty.name += " ";
      }
      invoiceParty.name += company.company;
    }

    invoiceParty.email = company.contact?.email;
    invoiceParty.phone = company.contact?.phone;

    if (company.billingAddress) {
      invoiceParty.address = new Address(company.billingAddress);
    }

    invoiceParty.taxnumber = company.taxnumber;

    this.seller = invoiceParty;
    this.sellerCompany = company;
  }
}

type IInvoice = InvoiceBase;
const Invoice = Filter.createForClass(InvoiceBase);

export { IInvoice, Invoice };
